import { BookingType, ProductType } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { ManageBookingContext, ManageBookingProps } from "./types";

export const useManageBookingContext = (
  props: ManageBookingProps
): ManageBookingContext => {
  const { itinerary, isActiveBooking, variant } = props;
  const { t } = useI18nContext();

  if (variant === ProductType.PACKAGE) {
    return {
      showExchange: false,
      showCancel: false,
      showCfarExercise: false,
      reservationId: itinerary.hopperReservationId,
      bookingType: BookingType.Package,
      cancelLabel: "",
      exchangeLabel: "",
      showDisruptionExercise: false,
    };
  }

  // TODO(Sarah): Implement self-serve exchange functionality
  // TODO: Control showExchange with feature flags
  if (variant === ProductType.LODGING) {
    const hasCfar = itinerary.cfar != null;
    return {
      showExchange: false,
      showCancel: isActiveBooking && !hasCfar,
      showCfarExercise: isActiveBooking && hasCfar,
      reservationId: itinerary.reservation.reservationBookingId,
      bookingType: BookingType.Lodging,
      cancelLabel: t("trips.tripReviewLinks.cancelStay"),
      exchangeLabel: t("hotelTripsLinks.changeReservation"),
      showDisruptionExercise: false,
    };
  }

  if (variant === ProductType.ACTIVITIES) {
    return {
      showExchange: false,
      showCancel: isActiveBooking,
      showCfarExercise: false,
      reservationId: itinerary.bookingSession?.cartOrderedProductId ?? "",
      bookingType: BookingType.Activities,
      cancelLabel: t("trips.tripReviewLinks.cancelActivity"),
      exchangeLabel: t("activitiesTripsLinks.changeActivity"),
      showDisruptionExercise: false,
    };
  }

  if (variant === ProductType.HOMES) {
    return {
      showExchange: false,
      showCancel: isActiveBooking,
      showCfarExercise: false,
      reservationId: itinerary.cartOrderedProductId,
      bookingType: BookingType.Homes,
      cancelLabel: t("trips.tripReviewLinks.cancelStay"),
      exchangeLabel: "",
      showDisruptionExercise: false,
    };
  }

  if (variant === ProductType.FLIGHTS) {
    const hasCfar = itinerary.cfar != null;
    return {
      showExchange: true,
      showCancel: isActiveBooking && !hasCfar,
      showCfarExercise: isActiveBooking && hasCfar,
      reservationId: itinerary.bookedItinerary?.id ?? "",
      bookingType: BookingType.Flight,
      cancelLabel: t("trips.tripReviewLinks.cancelFlight"),
      showDisruptionExercise: !!itinerary.ancillaries?.disruptionProtection,
      exchangeLabel: t("tripReviewLinks.changeFlight"),
    };
  }

  return {
    showExchange: true,
    showCancel: isActiveBooking,
    showCfarExercise: false,
    reservationId: "",
    bookingType: BookingType.Lodging,
    cancelLabel: t("trips.tripReviewLinks.cancelStay"),
    exchangeLabel: t("hotelTripsLinks.changeReservation"),
    showDisruptionExercise: false,
  };
};
